import { Formik } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TeamsModal } from '@components/FormModals/TeamsModal';
import {
  GetTeamDetailDocument,
  Team,
  TeamAttributes,
  useUpdateTeamMutation,
} from '@graphql/generated';
import useFileProcessor from '@hooks/useFileProcessor';
import { FormValues } from '@navigation/teams/create-team-stack';
import { createTeamSchema } from '@schemas/createTeamSchema';
import { mapLocalFilesToDocuments } from '@utils/fileUtils';

import type { Location } from '@components/LocationSearch/LocationSearch';

interface EditTeamModalProps {
  showCreateTeamModal: boolean;
  team: Team;
  setShowCreateTeamModal: (show: boolean) => void;
}

const EditTeamModal: React.FC<EditTeamModalProps> = ({
  showCreateTeamModal,
  team,
  setShowCreateTeamModal,
}) => {
  const initialValues: FormValues = {
    location:
      team?.address === null
        ? null
        : ({
            address: team?.address,
            latitude: team?.latitude,
            longitude: team?.longitude,
          } as Location),
    name: team?.name,
    skillIds: team?.skills ? team?.skills.map((t) => t.id) : [],
    logo: team?.avatarFile ? team.avatarFile : null,
  };

  const { t } = useTranslation();
  const { processFiles } = useFileProcessor();
  const [updateTeam, { data: updateTeamData, error: updateTeamError }] =
    useUpdateTeamMutation({
      refetchQueries: [
        ...(team?.uuid
          ? [
              {
                query: GetTeamDetailDocument,
                variables: { id: team?.uuid, includePending: true },
              },
            ]
          : []),
        'getTeam',
      ],
    });

  useEffect(() => {
    if (!updateTeamData) return;
    setShowCreateTeamModal(false);
  }, [updateTeamData]);

  useEffect(() => {
    if (!updateTeamError) return;
    setShowCreateTeamModal(false);
  }, [updateTeamError]);

  if (!showCreateTeamModal) return null;

  const submitForm = async (
    values: FormValues,
    setSubmitting: (a: boolean) => void
  ) => {
    setSubmitting(true);

    let avatarBlobId = undefined;

    if (values.logo && values.logo.__typename === 'LocalFile') {
      await processFiles(mapLocalFilesToDocuments([values.logo]))
        .then(async (response) => {
          avatarBlobId = response[0]?.blobId;
        })
        .catch((err) => {
          alert(err);
        });
    }

    const { name, location, skillIds } = values;
    const { address, latitude, longitude } = location || {
      address: undefined,
      latitude: undefined,
      longitude: undefined,
    };
    const attributes = {
      name,
      skillIds,
      address,
      latitude,
      longitude,
      avatarBlobId,
    } as TeamAttributes;

    await updateTeam({
      variables: {
        id: team.id,
        attributes,
      },
    });

    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createTeamSchema}
      onSubmit={async (values, { setSubmitting }) => {
        await submitForm(values, setSubmitting);
      }}>
      {() => (
        <TeamsModal
          title={t('models:teams.editTeam')}
          onClose={() => setShowCreateTeamModal(false)}
        />
      )}
    </Formik>
  );
};

export default EditTeamModal;
